import { Quote } from "lucide-react";
import { Reveal, CountUp } from "./primitives";

const STATS = [
  { value: 12, suffix: "+", label: "Years in solar" },
  { value: 30, suffix: "+", label: "MW installed" },
  { value: 100, suffix: "+", label: "Commercial projects" },
];

const CLIENTS = ["PGCIL", "GRSE", "NTPC", "Indian Railways", "SAIL"];

const TESTIMONIAL_CARDS = [
  {
    tag: "Commercial rooftop project",
    quote:
      "Sunshell helped us reduce our electricity expenses by building a solar PV rooftop power plant with utmost dedication and precision.",
    caption: "Facilities Head · Kolkata",
  },
  {
    tag: "Institutional confidence",
    quote:
      "12+ years in solar, 30+ MW installed and 100+ commercial projects delivered across demanding sites.",
    caption: "Verified project proof",
  },
  {
    tag: "Engineering-led delivery",
    quote:
      "The same disciplined solar execution built for institutions, shaped into a clearer experience for homes.",
    caption: "Sunshell track record",
  },
];

export const Proof = () => (
  <section
    id="proof"
    className="relative bg-navy-900 text-white py-24 sm:py-32 lg:py-40 overflow-hidden grain"
    data-testid="proof-section"
  >
    {/* connected-dot pattern */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.15]"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(217,119,6,0.6) 1px, transparent 1px)",
        backgroundSize: "38px 38px",
      }}
      aria-hidden
    />

    <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="eyebrow text-amber-400 mb-6">Proof, not promises</p>
      </Reveal>
      <Reveal delay={0.05}>
        <div className="mb-14 max-w-4xl">
          <h2 className="display-serif text-4xl leading-[1.04] tracking-[-0.03em] text-white sm:text-5xl lg:text-[3.6rem]">
            Institutional engineering. Built for your home.
          </h2>
          <p className="mt-5 text-lg leading-8 text-white/58">
            From Sunshell · 12+ years in solar · 30+ MW delivered · 100+ projects
          </p>
        </div>
      </Reveal>

      <div className="grid sm:grid-cols-3 gap-10 sm:gap-6 border-b border-white/10 pb-16">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div data-testid={`stat-${i}`}>
              <div className="display-serif text-6xl sm:text-7xl lg:text-8xl tracking-[-0.03em] text-white">
                <CountUp value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <p className="mt-3 text-white/55 text-lg">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* clients */}
      <Reveal delay={0.1}>
        <div className="py-12 flex flex-wrap items-center gap-x-10 gap-y-6">
          <span className="eyebrow text-white/40">Trusted by</span>
          {CLIENTS.map((c) => (
            <span
              key={c}
              className="text-xl sm:text-2xl font-semibold tracking-tight text-white/55 transition-colors duration-300 hover:text-white"
            >
              {c}
            </span>
          ))}
        </div>
      </Reveal>

      {/* testimonial */}
      <Reveal delay={0.05}>
        <div className="relative mt-6 overflow-hidden rounded-[1.8rem] border border-white/10 bg-white/[0.035] py-6 backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-navy-900 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-navy-900 to-transparent" />

          <div className="testimonial-scroll-track flex w-max gap-5 px-5 will-change-transform">
            {[...TESTIMONIAL_CARDS, ...TESTIMONIAL_CARDS].map((card, index) => (
              <figure
                key={`${card.tag}-${index}`}
                className="w-[82vw] max-w-[430px] shrink-0 rounded-[1.4rem] border border-white/12 bg-white/[0.075] p-7 shadow-2xl shadow-black/15 backdrop-blur-md sm:w-[430px]"
                aria-hidden={index >= TESTIMONIAL_CARDS.length}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="eyebrow text-amber-400/85">{card.tag}</span>
                  <Quote className="shrink-0 text-amber" size={28} />
                </div>
                <blockquote className="mt-8 display-serif text-2xl leading-[1.34] text-white/90">
                  “{card.quote}”
                </blockquote>
                <figcaption className="mt-7 border-t border-white/10 pt-5 text-sm font-medium text-white/48">
                  {card.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
